import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { sprintsApi, type Sprint, type SprintStatus } from '../sprints/sprintsApi';
import { tasksApi, type Task } from '../tasks/tasksApi';
import VelocityChart from '../components/VelocityChart';
import { PencilIcon } from '../components/icons';

const SPRINT_STATUSES: SprintStatus[] = ['PLANNED', 'ACTIVE', 'CLOSED'];

export default function SprintsPage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const { getToken, refresh } = useAuth();

  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [newName, setNewName] = useState('');
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editStatus, setEditStatus] = useState<SprintStatus>('PLANNED');
  const [saving, setSaving] = useState(false);
  const [closingId, setClosingId] = useState<string | null>(null);
  const [sprintTasksMap, setSprintTasksMap] = useState<Record<string, Task[]>>({});
  const loadedSprintIds = useRef<Set<string>>(new Set());

  const loadSprints = useCallback(() => {
    setLoading(true);
    sprintsApi
      .list(workspaceId, getToken, refresh)
      .then(data => setSprints(data.sprints))
      .catch(() => setError('Failed to load sprints'))
      .finally(() => setLoading(false));
  }, [workspaceId, getToken, refresh]);

  useEffect(() => {
    loadSprints();
  }, [loadSprints]);

  useEffect(() => {
    for (const sprint of sprints) {
      if (sprint.status !== 'CLOSED') continue;
      if (loadedSprintIds.current.has(sprint.sprintId)) continue;
      loadedSprintIds.current.add(sprint.sprintId);
      tasksApi
        .listForSprint(workspaceId, sprint.sprintId, getToken, refresh)
        .then(data => setSprintTasksMap(prev => ({ ...prev, [sprint.sprintId]: data.tasks })))
        .catch(() => { /* optional */ });
    }
  }, [sprints, workspaceId, getToken, refresh]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!newName.trim() || !newStartDate || !newEndDate) return;
    setCreating(true);
    setError('');
    try {
      await sprintsApi.create(workspaceId, newName.trim(), newStartDate, newEndDate, getToken, refresh);
      setNewName('');
      setNewStartDate('');
      setNewEndDate('');
      setShowCreateForm(false);
      loadSprints();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create sprint');
    } finally {
      setCreating(false);
    }
  }

  function startEdit(sprint: Sprint) {
    setEditingId(sprint.sprintId);
    setEditName(sprint.name);
    setEditStartDate(sprint.startDate);
    setEditEndDate(sprint.endDate);
    setEditStatus(sprint.status);
  }

  async function handleSave(e: FormEvent, sprintId: string) {
    e.preventDefault();
    if (!editName.trim() || !editStartDate || !editEndDate) return;
    setSaving(true);
    setError('');
    try {
      const updated = await sprintsApi.update(
        workspaceId,
        sprintId,
        { name: editName.trim(), startDate: editStartDate, endDate: editEndDate, status: editStatus },
        getToken,
        refresh,
      );
      setSprints(prev => prev.map(s => (s.sprintId === sprintId ? updated : s)));
      setEditingId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update sprint');
    } finally {
      setSaving(false);
    }
  }

  async function handleClose(sprintId: string) {
    setClosingId(sprintId);
    setError('');
    try {
      const updated = await sprintsApi.close(workspaceId, sprintId, getToken, refresh);
      setSprints(prev => prev.map(s => (s.sprintId === sprintId ? updated : s)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close sprint');
    } finally {
      setClosingId(null);
    }
  }

  return (
    <main className="page">
      <div className="toolbar" style={{ justifyContent: 'space-between' }}>
        <h1>Sprints</h1>
        {!showCreateForm && (
          <button type="button" onClick={() => setShowCreateForm(true)}>
            + Create sprint
          </button>
        )}
      </div>

      {error && <p className="error">{error}</p>}

      {showCreateForm && (
        <form onSubmit={handleCreate} className="form-card">
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Sprint name" required autoFocus />
          <label>
            Start date
            <input type="date" value={newStartDate} onChange={e => setNewStartDate(e.target.value)} required />
          </label>
          <label>
            End date
            <input type="date" value={newEndDate} onChange={e => setNewEndDate(e.target.value)} required />
          </label>
          <div className="form-actions">
            <button type="submit" disabled={creating}>
              {creating ? 'Creating…' : 'Create sprint'}
            </button>
            <button type="button" onClick={() => setShowCreateForm(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading && <p className="muted">Loading…</p>}
      {!loading && sprints.length === 0 && <p className="muted">No sprints yet.</p>}

      {sprints.some(s => s.status === 'CLOSED') && (
        <VelocityChart
          data={sprints
            .filter(s => s.status === 'CLOSED')
            .map(sprint => {
              const tasks = sprintTasksMap[sprint.sprintId] ?? [];
              return {
                sprint,
                totalPoints: tasks.reduce((s, t) => s + t.points, 0),
                completedPoints: tasks.filter(t => t.completedAt != null).reduce((s, t) => s + t.points, 0),
              };
            })}
        />
      )}

      <ul className="card-list">
        {sprints.map(sprint => (
          <li key={sprint.sprintId} className="card">
            {editingId === sprint.sprintId ? (
              <form onSubmit={e => handleSave(e, sprint.sprintId)} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Sprint name" required />
                <label>
                  Start date
                  <input type="date" value={editStartDate} onChange={e => setEditStartDate(e.target.value)} required />
                </label>
                <label>
                  End date
                  <input type="date" value={editEndDate} onChange={e => setEditEndDate(e.target.value)} required />
                </label>
                <select value={editStatus} onChange={e => setEditStatus(e.target.value as SprintStatus)}>
                  {SPRINT_STATUSES.map(status => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                <div className="form-actions">
                  <button type="submit" disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" onClick={() => setEditingId(null)}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <strong>{sprint.name}</strong>
                <p className="muted" style={{ margin: '4px 0' }}>
                  {sprint.status} — {sprint.startDate} to {sprint.endDate}
                </p>
                <div className="toolbar--split">
                  <div className="toolbar">
                    <button type="button" className="icon-btn" title="Edit sprint" onClick={() => startEdit(sprint)}>
                      <PencilIcon />
                    </button>
                    <Link to={`/workspaces/${workspaceId}/sprints/${sprint.sprintId}/board`}>
                      <button type="button">Board view</button>
                    </Link>
                  </div>
                  {sprint.status !== 'CLOSED' && (
                    <button type="button" onClick={() => handleClose(sprint.sprintId)} disabled={closingId === sprint.sprintId}>
                      {closingId === sprint.sprintId ? 'Closing…' : 'Close sprint'}
                    </button>
                  )}
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
    </main>
  );
}
